// src/components/Contact.js
import React, { useState, useRef, useEffect } from 'react';
import './Contact.css';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const contactRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init("YOUR_PUBLIC_KEY"); // Get from EmailJS dashboard
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (contactRef.current) {
      observer.observe(contactRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Replace these with your actual EmailJS service ID, template ID, and public key
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        message: formData.message,
        to_name: 'KyroTech Team',
        reply_to: formData.email,
      };

      await emailjs.send(
        'YOUR_SERVICE_ID', // Service ID from EmailJS
        'YOUR_TEMPLATE_ID', // Template ID from EmailJS
        templateParams,
        'YOUR_PUBLIC_KEY' // Public Key from EmailJS
      );

      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);

    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contact" ref={contactRef}>
      <div className="contact-container">
        <div className={`contact-header ${isVisible ? 'visible' : ''}`}>
          <span className="section-number">03</span>
          <h2 className="section-title">Let's Work Together</h2>
          <p className="section-description">
            Ready to start your next project? Get in touch and let's create something amazing.
          </p>
        </div>

        <div className="contact-content">
          <div className={`contact-info ${isVisible ? 'visible' : ''}`}>
            <div className="info-card">
              <div className="info-icon">
                <Mail size={28} />
              </div>
              <div className="info-text">
                <h3>Email Us</h3>
                <a href="mailto:hello@kyrotech.in">hello@kyrotech.in</a>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <Phone size={28} />
              </div>
              <div className="info-text">
                <h3>Call Us</h3>
                <a href="tel:+91 8140320831">+91 814032081</a>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <MapPin size={28} />
              </div>
              <div className="info-text">
                <h3>Visit Us</h3>
                <p>E-1405 New ganesh glory, gota, 382481 Ahmedabad</p>
              </div>
            </div>

            
          </div>

          <div className={`contact-form-wrapper ${isVisible ? 'visible' : ''}`}>
            <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
              {submitStatus === 'success' && (
                <div className="form-success">
                  <p>✅ Message sent successfully! We'll get back to you soon.</p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="form-error">
                  <p>❌ Failed to send message. Please try again or contact us directly.</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name">Your Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 8140320831"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Tell us about your project..."
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;